import React, { useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import type { PlayerSummary } from 'types/fpl/elements'
import { getPlayers } from 'api/league'
import { filterPlayers } from 'utils/players'
import { shirtImageSrc } from 'utils/shirtSrc'
import { colors } from 'constants/colors'

interface ExcludePlayersAutocompleteProps {
  excludedPlayers: PlayerSummary[]
  setExcludedPlayers: (players: PlayerSummary[]) => void
  isMobile: boolean
}

const ExcludePlayersAutocomplete: React.FC<ExcludePlayersAutocompleteProps> = ({
  excludedPlayers,
  setExcludedPlayers,
  isMobile,
}) => {
  const [allPlayers, setAllPlayers] = useState<PlayerSummary[]>([])

  useEffect(() => {
    getPlayers().then(players => {
      const unique = Array.from(new Map(players.map(p => [String(p.id), p])).values())
      setAllPlayers(unique)
    })
  }, [])

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
      <Typography
        variant='subtitle1'
        fontFamily='PremierLeagueBold'
        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 1 }}
      >
        Exclude Players
      </Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={allPlayers}
        value={excludedPlayers}
        onChange={(_, newValue) => setExcludedPlayers(newValue)}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={filterPlayers}
        size={isMobile ? 'small' : 'medium'}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={excludedPlayers.length === 0 ? 'Search players…' : ''}
            size={isMobile ? 'small' : 'medium'}
            InputProps={{
              ...params.InputProps,
              style: {
                fontFamily: 'PremierLeagueRegular',
                fontSize: isMobile ? '0.75rem' : '0.875rem',
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => {
          const { key: _key, ...restProps } = props as any
          return (
            <Box
              component='li'
              key={option.id}
              {...restProps}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Checkbox checked={selected} size='small' sx={{ p: 0.5 }} />
              <Box
                component='img'
                src={shirtImageSrc(option.team_id)}
                alt={option.team_name}
                sx={{ width: 20, height: 24, objectFit: 'contain', flexShrink: 0 }}
              />
              <Typography fontFamily='PremierLeagueRegular' sx={{ fontSize: '0.875rem' }}>
                {option.name}
              </Typography>
              <Typography
                fontFamily='PremierLeagueRegular'
                sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 'auto' }}
              >
                {option.team_name}
              </Typography>
            </Box>
          )
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.length <= 2 ? (
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index })
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  size='small'
                  avatar={
                    <Box
                      component='img'
                      src={shirtImageSrc(option.team_id)}
                      alt={option.team_name}
                      sx={{ width: 16, height: 20, objectFit: 'contain' }}
                    />
                  }
                  label={
                    <Typography fontFamily='PremierLeagueRegular' sx={{ fontSize: '0.75rem' }}>
                      {option.name}
                    </Typography>
                  }
                />
              )
            })
          ) : (
            <Box sx={{ display: 'flex', backgroundColor: '#f0f0f0', borderRadius: 3, alignItems: 'center', gap: 0.25, ml: 0.5 }}>
              <Typography fontFamily='PremierLeagueRegular' sx={{ fontSize: '0.8em', color: colors.ALMOST_BLACK, lineHeight: 1, p: 1 }}>
                {tagValue.length} players excluded
              </Typography>
            </Box>
          )
        }
      />
    </Paper>
  )
}

export default ExcludePlayersAutocomplete
